/**
 * Utility functions and constants for DrugDetailPage
 */

// Section name mapping (FDA standard sections)
export const SECTION_NUMBER_NAMES: Record<string, string> = {
  '1': 'INDICATIONS AND USAGE',
  '2': 'DOSAGE AND ADMINISTRATION',
  '3': 'DOSAGE FORMS AND STRENGTHS',
  '4': 'CONTRAINDICATIONS',
  '5': 'WARNINGS AND PRECAUTIONS',
  '6': 'ADVERSE REACTIONS',
  '7': 'DRUG INTERACTIONS',
  '8': 'USE IN SPECIFIC POPULATIONS',
  '9': 'DRUG ABUSE AND DEPENDENCE',
  '10': 'OVERDOSAGE',
  '11': 'DESCRIPTION',
  '12': 'CLINICAL PHARMACOLOGY',
  '13': 'NONCLINICAL TOXICOLOGY',
  '14': 'CLINICAL STUDIES',
  '15': 'REFERENCES',
  '16': 'HOW SUPPLIED/STORAGE AND HANDLING',
  '17': 'PATIENT COUNSELING INFORMATION',
  'BW': 'BOXED WARNING',
  'MISC': 'MISCELLANEOUS',
};

/**
 * Format timestamp as relative time string
 */
export function formatRelativeTime(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

/**
 * Parse section number into comparable parts for version-style sorting
 * e.g., "1.10" -> [1, 10], "1.2" -> [1, 2], so 1.2 < 1.10
 */
export function parseSectionParts(sectionNumber: string | null | undefined): number[] {
  if (!sectionNumber) return [99];
  if (sectionNumber === 'BW') return [-1];
  if (sectionNumber === '0') return [0];
  if (sectionNumber === 'MISC') return [100];
  // UK Patient Information Leaflet is appended below the SmPC (sections
  // 1-10). "PIL" is the umbrella row, "PIL.0" the intro framing block, and
  // "PIL.1".."PIL.6" the numbered patient sections. parseInt("PIL") is NaN,
  // so the naive split() path mapped both "PIL" and "PIL.0" to [0] — they
  // tied under compareSectionNumbers and the alphabetical title tiebreak put
  // "Package leaflet…" (PIL.0) ABOVE the "Patient Leaflet" (PIL) header, and
  // the whole block floated to the top (before §1). Sort the block as a unit
  // after §10 (base 90, below MISC=100 / unlabeled=99), umbrella first.
  if (sectionNumber === 'PIL') return [90, -1];
  const pilMatch = sectionNumber.match(/^PIL\.(\d+)$/);
  if (pilMatch) return [90, parseInt(pilMatch[1]!, 10) || 0];
  return sectionNumber.split('.').map(part => parseInt(part, 10) || 0);
}

/**
 * Check if a section is HIGHLIGHTS OF PRESCRIBING INFORMATION
 */
export function isHighlightsSection(section: {
  title?: string | null;
  loinc_code?: string | null;
  section_type?: string | null
}): boolean {
  return section.loinc_code === 'HIGHLIGHTS' ||
         section.section_type === 'HIGHLIGHTS_OF_PRESCRIBING_INFORMATION' ||
         section.title?.toUpperCase().includes('HIGHLIGHTS OF PRESCRIBING INFORMATION') === true;
}

/**
 * Compare two section numbers using version-style comparison
 */
export function compareSectionNumbers(a: string | null | undefined, b: string | null | undefined): number {
  const aParts = parseSectionParts(a);
  const bParts = parseSectionParts(b);
  const maxLen = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < maxLen; i++) {
    const aVal = aParts[i] ?? 0;
    const bVal = bParts[i] ?? 0;
    if (aVal !== bVal) return aVal - bVal;
  }
  return 0;
}

/**
 * Get the base/parent section number (e.g., "2.1" -> "2", "8" -> "8")
 */
export function getParentSectionNum(section: {
  section_number?: string | null;
  title?: string | null
}): string {
  const titleMatch = section.title?.match(/^(\d+)\.\d+/);
  if (titleMatch?.[1]) return titleMatch[1];
  if (section.section_number?.includes('.')) {
    return section.section_number.split('.')[0] ?? '';
  }
  return section.section_number ?? '';
}

/**
 * Detect if a section is a subsection based on section_number or title
 */
export function isSubsection(section: {
  section_number?: string | null;
  title?: string | null
}): boolean {
  if (section.section_number?.includes('.')) return true;
  if (section.title && /^\d+\.\d+/.test(section.title)) return true;
  return false;
}

/**
 * Common stop words to filter out from search highlights
 */
export const HIGHLIGHT_STOP_WORDS = new Set([
  'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
  'should', 'may', 'might', 'must', 'shall', 'can', 'to', 'of', 'in',
  'for', 'on', 'with', 'at', 'by', 'from', 'as', 'and', 'but', 'if',
  'or', 'because', 'until', 'while', 'what', 'which', 'who', 'this',
  'that', 'these', 'those', 'drug', 'drugs', 'show', 'find', 'list',
  'all', 'any', 'also', 'about', 'after', 'before', 'between', 'into',
  'through', 'during', 'each', 'few', 'more', 'most', 'other', 'some',
  'such', 'only', 'same', 'than', 'too', 'very', 'just', 'how', 'when',
  'where', 'why', 'get', 'got', 'use', 'used', 'using', 'not', 'out'
]);
